import { z } from "zod";

/**
 * Phase 5 — Bepreisung (deterministisch, KEIN LLM).
 *
 * Aus Listenpreis × (1 - Rabatt) ODER Sonderpreis aus Konditionsmatrix.
 * Marge konfigurierbar pro Tenant. Rundung 2 Dezimalstellen.
 *
 * `quelle = "schnellkalkulation"` markiert Positionen, deren Einzelpreis aus
 * mehreren Komponenten (Material + Lohn + Sonstiges …) aggregiert wurde.
 * Die Anteils-Felder summieren dann zum Einzelpreis (Toleranz 0,01 EUR) und
 * speisen den EFB-223-Export (Excel-Sheet „Aufgliederung Einheitspreise"
 * + GAEB DA84 Aufgliederung-Tags).
 *
 * Fuer klassische 1-Artikel-Picks (`quelle = "stammartikel_einfach"`) sind
 * die Anteile defensiv leer (null) — der Excel-Export faellt dann auf
 * „voll Material" zurueck.
 */
export const PreisPositionSchema = z.object({
  positionId: z.string(),
  artikelCode: z.string().nullable(),
  listenpreis: z.number().nullable(),
  rabattProzent: z.number().min(0).max(100).default(0),
  sonderpreis: z.number().nullable().default(null),
  margeProzent: z.number().default(0),
  einzelpreis: z.number().nullable(),
  gesamtpreis: z.number().nullable(),
  bepreisbar: z.boolean(),
  hinweis: z.string().optional(),
  /** Bepreisungs-Pfad. `stammartikel_einfach` = klassischer 1-Artikel-Pick;
   *  `schnellkalkulation` = aus Komponentenzeilen aggregiert. Default
   *  `stammartikel_einfach` damit alte JSON-Files unveraendert lesbar
   *  bleiben. */
  quelle: z
    .enum(["stammartikel_einfach", "schnellkalkulation"])
    .default("stammartikel_einfach"),
  /** EFB-223-Aufgliederung pro Einheit. Bei `quelle = "schnellkalkulation"`
   *  ist Σ Anteile == einzelpreis (Toleranz 0,01 EUR, Invariante im Agent
   *  geprueft). Bei `stammartikel_einfach` sind alle Anteile `null` und der
   *  Excel-Export weist den vollen EP als Material aus. */
  anteilLohn: z.number().nullable().default(null),
  anteilMaterial: z.number().nullable().default(null),
  anteilGeraet: z.number().nullable().default(null),
  anteilSonstige: z.number().nullable().default(null),
  anteilFremd: z.number().nullable().default(null),
  /** Schritt 2 (Basiseinheit-Modell): tatsächlich mit `einzelpreis` multiplizierte
   *  Menge in der Artikel-Verkaufseinheit — z.B. 2 (Sack) statt 50 (kg). Nur
   *  gesetzt, wenn eine Gebinde→Basis-Umrechnung stattfand bzw. = `pos.menge`
   *  bei alias-gleicher Einheit. Macht das Angebot/EFB-223 konsistent (sonst
   *  stünde „menge in kg × Preis pro Sack"). `undefined` bei alten Files. */
  mengeBerechnet: z.number().optional(),
  /** Einheit, auf die sich `einzelpreis` × `mengeBerechnet` bezieht
   *  (= Artikel-Verkaufseinheit). */
  berechnungsEinheit: z.string().optional(),
});
export type PreisPosition = z.infer<typeof PreisPositionSchema>;

export const PreiseSchema = z.object({
  /** v2 (2026-05): Aufgliederungs-Anteile + `quelle`. v1-Dateien lesen
   *  weiter dank Defaults. */
  schemaVersion: z.union([z.literal(1), z.literal(2)]).default(2),
  positionen: z.array(PreisPositionSchema).default([]),
  summeNetto: z.number().default(0),
  waehrung: z.string().default("EUR"),
});

export type Preise = z.infer<typeof PreiseSchema>;
